import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris';
import type { AuditFrequency } from '../../api/auditSchedule';
import {
  useAuditSchedule,
  useDeleteAuditSchedule,
  useUpsertAuditSchedule,
} from '../../hooks/useAuditSchedule';

const FREQUENCY_OPTIONS = [
  { label: 'Disabled', value: 'off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly (every Monday)', value: 'weekly' },
  { label: 'Monthly (1st of month)', value: 'monthly' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: `${String(i).padStart(2, '0')}:00`,
  value: String(i),
}));

export function AuditScheduleConfig() {
  const { data: schedule, isLoading } = useAuditSchedule();
  const upsert = useUpsertAuditSchedule();
  const disable = useDeleteAuditSchedule();

  const [frequency, setFrequency] = useState<AuditFrequency>('off');
  const [runAtHour, setRunAtHour] = useState('8');
  const [timezone, setTimezone] = useState('UTC');
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (schedule) {
      setFrequency(schedule.frequency);
      setRunAtHour(String(schedule.run_at_hour));
      setTimezone(schedule.timezone);
    }
  }, [schedule]);

  const handleSave = useCallback(async () => {
    setError(null);
    try {
      if (frequency === 'off') {
        await disable.mutateAsync();
        setToast('Audit schedule disabled.');
      } else {
        await upsert.mutateAsync({
          frequency,
          run_at_hour: Number(runAtHour),
          timezone,
        });
        setToast('Audit schedule saved.');
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save audit schedule.');
    }
  }, [frequency, runAtHour, timezone, upsert, disable]);

  if (isLoading) {
    return (
      <Card>
        <SkeletonBodyText lines={4} />
      </Card>
    );
  }

  const isBusy = upsert.isPending || disable.isPending;

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          Scheduled Audits
        </Text>

        <Text as="p" tone="subdued">
          Automatically run a store health audit on a recurring basis. Audits are dispatched at
          the configured hour in your chosen timezone.
        </Text>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
          <Select
            label="Frequency"
            options={FREQUENCY_OPTIONS}
            value={frequency}
            onChange={(v) => setFrequency(v as AuditFrequency)}
          />

          <Select
            label="Run at hour"
            options={HOUR_OPTIONS}
            value={runAtHour}
            onChange={setRunAtHour}
            disabled={frequency === 'off'}
          />

          <TextField
            label="Timezone"
            value={timezone}
            onChange={setTimezone}
            autoComplete="off"
            helpText="e.g. America/New_York"
            disabled={frequency === 'off'}
          />
        </InlineGrid>

        {schedule?.is_enabled && schedule.next_run_at && (
          <Text as="p" tone="subdued">
            Next scheduled run:{' '}
            <strong>{new Date(schedule.next_run_at).toLocaleString()}</strong>
          </Text>
        )}

        <Box>
          <Button variant="primary" onClick={handleSave} loading={isBusy}>
            Save Schedule
          </Button>
        </Box>

        {toast && (
          <Toast content={toast} onDismiss={() => setToast(null)} duration={3000} />
        )}
      </BlockStack>
    </Card>
  );
}
